import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Eye, 
  CheckCircle, 
  XCircle,
  Clock,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  UserPlus,
  Upload,
  Download,
  Power,
  PowerOff
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';
import { logger } from '../../utils/logger';
import { 
  RELIGION_OPTIONS, 
  MOTHER_TONGUE_OPTIONS, 
  HEIGHT_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  EMPLOYMENT_TYPES,
  INCOME_RANGES,
  DIET_OPTIONS,
  HABIT_OPTIONS
} from '../../lib/constants';
import { states, cities } from '../../utils/locationData';

interface DummyProfile {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  profileId: {
    _id: string;
    gender: string;
    age: number;
    city: string;
    state: string;
    religion: string;
    community: string;
    photos: string[];
  };
  isActive: boolean;
  autoAcceptInterests: {
    enabled: boolean;
    delayHours: number;
  };
  autoReplyMessages: {
    enabled: boolean;
    messages: Array<{ text: string; delayMinutes: number }>;
    defaultMessages: string[];
  };
  displayPriority: number;
  stats: {
    totalViews: number;
    interestsReceived: number;
    interestsAccepted: number;
    messagesReceived: number;
    messagesSent: number;
  };
  adminNotes: string;
  createdAt: string;
}

interface RealProfile {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  gender: string;
  age: number;
  city: string;
  state: string;
  country: string;
  religion: string;
  community: string;
  maritalStatus: string;
  height: number;
  weight: number;
  education: any;
  occupation: string;
  employmentType: string;
  annualIncome: string;
  motherTongue: string;
  about: string;
  photos: string[];
  family: any;
  diet: string;
  smoking: string;
  drinking: string;
}

export default function AdminDummyProfiles() {
  const [dummyProfiles, setDummyProfiles] = useState<DummyProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showMultipleRepliesDialog, setShowMultipleRepliesDialog] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<DummyProfile | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);

  // Search real profiles
  const [searchResults, setSearchResults] = useState<RealProfile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [profileSearchQuery, setProfileSearchQuery] = useState('');

  // Bulk import
  const [bulkImportData, setBulkImportData] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: 'female',
    dateOfBirth: '',
    city: '',
    state: '',
    country: 'India',
    religion: '',
    community: '',
    maritalStatus: 'never_married',
    height: 160,
    weight: 55,
    education: {
      level: 'bachelors',
      field: '',
      institution: ''
    },
    occupation: '',
    employmentType: 'private',
    annualIncome: '5-7',
    motherTongue: '',
    about: '',
    photos: [] as string[],
    family: {},
    diet: 'vegetarian',
    smoking: 'no',
    drinking: 'no',
    isActive: true,
    autoAcceptEnabled: true,
    autoAcceptDelayHours: 2,
    autoReplyEnabled: true,
    autoReplyMessages: ['Hi! 😊', 'How are you?', 'Thanks for showing interest!'],
    displayPriority: 1000,
    adminNotes: ''
  });

  useEffect(() => {
    fetchDummyProfiles();
    fetchStats();
  }, []);

  const fetchDummyProfiles = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/dummy-profile');
      setDummyProfiles(response.data.data || []);
    } catch (error: any) {
      logger.error('Error fetching dummy profiles:', error);
      toast.error('Failed to fetch dummy profiles');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/dummy-profile/stats');
      setStats(response.data.data);
    } catch (error: any) {
      logger.error('Error fetching stats:', error);
    }
  };

  const searchRealProfiles = async () => {
    if (!profileSearchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    try {
      setSearchLoading(true);
      const response = await apiClient.get('/admin/users', {
        params: {
          search: profileSearchQuery,
          limit: 20,
          role: 'user'
        }
      });
      
      // Get users from the response - handle both possible structures
      const users = response.data.data?.users || response.data.users || [];
      
      if (users.length === 0) {
        toast.info('No users found matching your search');
        setSearchResults([]);
        return;
      }
      
      // Fetch full profile data for each user
      const profilesPromises = users.map(async (user: any) => {
        try {
          const profileRes = await apiClient.get(`/profiles/user/${user._id}`);
          if (profileRes.data?.data) {
            return {
              ...profileRes.data.data,
              userId: {
                _id: user._id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phone: user.phone
              }
            };
          }
          return null;
        } catch (err) {
          logger.error('Error fetching profile for user:', user._id, err);
          return null;
        }
      });
      
      const profiles = (await Promise.all(profilesPromises)).filter(p => p !== null);
      setSearchResults(profiles as RealProfile[]);
      
      if (profiles.length === 0) {
        toast.info('No complete profiles found matching your search');
      } else {
        toast.success(`Found ${profiles.length} profile(s)`);
      }
    } catch (error: any) {
      logger.error('Error searching profiles:', error);
      toast.error('Failed to search profiles');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const createFromRealProfile = (realProfile: RealProfile) => {
    const dob = new Date();
    dob.setFullYear(dob.getFullYear() - realProfile.age);

    setFormData({
      firstName: realProfile.userId.firstName,
      lastName: realProfile.userId.lastName,
      email: `dummy${Date.now()}@atootbandan.com`,
      phone: `+91${Math.floor(1000000000 + Math.random() * 9000000000)}`,
      gender: realProfile.gender,
      dateOfBirth: dob.toISOString().split('T')[0],
      city: realProfile.city,
      state: realProfile.state,
      country: realProfile.country,
      religion: realProfile.religion,
      community: realProfile.community,
      maritalStatus: realProfile.maritalStatus,
      height: realProfile.height,
      weight: realProfile.weight,
      education: realProfile.education || {
        level: 'bachelors',
        field: '',
        institution: ''
      },
      occupation: realProfile.occupation,
      employmentType: realProfile.employmentType,
      annualIncome: realProfile.annualIncome,
      motherTongue: realProfile.motherTongue,
      about: realProfile.about,
      photos: realProfile.photos,
      family: realProfile.family || {},
      diet: realProfile.diet,
      smoking: realProfile.smoking,
      drinking: realProfile.drinking,
      isActive: true,
      autoAcceptEnabled: true,
      autoAcceptDelayHours: 2,
      autoReplyEnabled: true,
      autoReplyMessages: ['Hi! 😊', 'How are you?', 'Thanks for showing interest!'],
      displayPriority: 1000,
      adminNotes: `Created from real profile: ${realProfile.userId.firstName} ${realProfile.userId.lastName}`
    });

    setShowSearchDialog(false);
    setShowCreateDialog(true);
    toast.success('Profile data loaded! You can now modify and create.');
  };

  const handleCreateDummyProfile = async () => {
    try {
      setLoading(true);

      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone
      };

      const profileData = {
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        religion: formData.religion,
        community: formData.community,
        maritalStatus: formData.maritalStatus,
        height: formData.height,
        weight: formData.weight,
        education: formData.education,
        occupation: formData.occupation,
        employmentType: formData.employmentType,
        annualIncome: formData.annualIncome,
        motherTongue: formData.motherTongue,
        about: formData.about,
        photos: formData.photos,
        family: formData.family,
        diet: formData.diet,
        smoking: formData.smoking,
        drinking: formData.drinking
      };

      const settings = {
        isActive: formData.isActive,
        autoAcceptInterests: {
          enabled: formData.autoAcceptEnabled,
          delayHours: formData.autoAcceptDelayHours
        },
        autoReplyMessages: {
          enabled: formData.autoReplyEnabled,
          messages: formData.autoReplyMessages.map((text, index) => ({
            text,
            delayMinutes: (index + 1) * 5
          })),
          defaultMessages: formData.autoReplyMessages
        },
        displayPriority: formData.displayPriority,
        adminNotes: formData.adminNotes
      };

      await apiClient.post('/dummy-profile', {
        userData,
        profileData,
        settings
      });

      toast.success('Dummy profile created successfully');
      setShowCreateDialog(false);
      fetchDummyProfiles();
      fetchStats();
      resetForm();
    } catch (error: any) {
      logger.error('Error creating dummy profile:', error);
      toast.error(error.response?.data?.message || 'Failed to create dummy profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDummyProfile = async () => {
    if (!selectedProfile) return;

    try {
      setLoading(true);

      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone
      };

      const profileData = {
        city: formData.city,
        state: formData.state,
        country: formData.country,
        religion: formData.religion,
        community: formData.community,
        maritalStatus: formData.maritalStatus,
        height: formData.height,
        weight: formData.weight,
        education: formData.education,
        occupation: formData.occupation,
        employmentType: formData.employmentType,
        annualIncome: formData.annualIncome,
        motherTongue: formData.motherTongue,
        about: formData.about,
        photos: formData.photos,
        family: formData.family,
        diet: formData.diet,
        smoking: formData.smoking,
        drinking: formData.drinking
      };

      const settings = {
        isActive: formData.isActive,
        autoAcceptInterests: {
          enabled: formData.autoAcceptEnabled,
          delayHours: formData.autoAcceptDelayHours
        },
        autoReplyMessages: {
          enabled: formData.autoReplyEnabled,
          messages: formData.autoReplyMessages.map((text, index) => ({
            text,
            delayMinutes: (index + 1) * 5
          })),
          defaultMessages: formData.autoReplyMessages
        },
        displayPriority: formData.displayPriority,
        adminNotes: formData.adminNotes
      };

      await apiClient.put(`/dummy-profile/${selectedProfile._id}`, {
        userData,
        profileData,
        settings
      });

      toast.success('Dummy profile updated successfully');
      setShowEditDialog(false);
      setSelectedProfile(null);
      fetchDummyProfiles();
      fetchStats();
      resetForm();
    } catch (error: any) {
      logger.error('Error updating dummy profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update dummy profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDummyProfile = async (id: string) => {
    if (!confirm('Are you sure you want to delete this dummy profile? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await apiClient.delete(`/dummy-profile/${id}`);
      toast.success('Dummy profile deleted successfully');
      fetchDummyProfiles();
      fetchStats();
    } catch (error: any) {
      logger.error('Error deleting dummy profile:', error);
      toast.error('Failed to delete dummy profile');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await apiClient.post(`/dummy-profile/${id}/toggle`);
      toast.success('Status updated successfully');
      fetchDummyProfiles();
      fetchStats();
    } catch (error: any) {
      logger.error('Error toggling status:', error);
      toast.error('Failed to toggle status');
    }
  };

  const processAutoAccepts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.post('/dummy-profile/process-auto-accepts');
      toast.success(response.data.message || 'Auto-accepts processed successfully');
      fetchDummyProfiles();
      fetchStats();
    } catch (error: any) {
      logger.error('Error processing auto-accepts:', error);
      toast.error('Failed to process auto-accepts');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (profile: DummyProfile) => {
    setSelectedProfile(profile);
    
    // Calculate date of birth from profile
    let dateOfBirth = '';
    if (profile.profileId.dateOfBirth) {
      dateOfBirth = new Date(profile.profileId.dateOfBirth).toISOString().split('T')[0];
    }

    setFormData({
      firstName: profile.userId.firstName,
      lastName: profile.userId.lastName,
      email: profile.userId.email,
      phone: profile.userId.phone,
      gender: profile.profileId.gender || 'female',
      dateOfBirth: dateOfBirth,
      city: profile.profileId.city || '',
      state: profile.profileId.state || '',
      country: (profile.profileId as any).country || 'India',
      religion: profile.profileId.religion || '',
      community: profile.profileId.community || '',
      maritalStatus: (profile.profileId as any).maritalStatus || 'never_married',
      height: (profile.profileId as any).height || 160,
      weight: (profile.profileId as any).weight || 55,
      education: (profile.profileId as any).education || {
        level: 'bachelors',
        field: '',
        institution: ''
      },
      occupation: (profile.profileId as any).occupation || '',
      employmentType: (profile.profileId as any).employmentType || 'private',
      annualIncome: (profile.profileId as any).annualIncome || '5-7',
      motherTongue: (profile.profileId as any).motherTongue || '',
      about: (profile.profileId as any).about || '',
      photos: profile.profileId.photos || [],
      family: (profile.profileId as any).family || {},
      diet: (profile.profileId as any).diet || 'vegetarian',
      smoking: (profile.profileId as any).smoking || 'no',
      drinking: (profile.profileId as any).drinking || 'no',
      isActive: profile.isActive,
      autoAcceptEnabled: profile.autoAcceptInterests.enabled,
      autoAcceptDelayHours: profile.autoAcceptInterests.delayHours,
      autoReplyEnabled: profile.autoReplyMessages.enabled,
      autoReplyMessages: profile.autoReplyMessages.defaultMessages,
      displayPriority: profile.displayPriority,
      adminNotes: profile.adminNotes || ''
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gender: 'female',
      dateOfBirth: '',
      city: '',
      state: '',
      country: 'India',
      religion: '',
      community: '',
      maritalStatus: 'never_married',
      height: 160,
      weight: 55,
      education: {
        level: 'bachelors',
        field: '',
        institution: ''
      },
      occupation: '',
      employmentType: 'private',
      annualIncome: '5-7',
      motherTongue: '',
      about: '',
      photos: [],
      family: {},
      diet: 'vegetarian',
      smoking: 'no',
      drinking: 'no',
      isActive: true,
      autoAcceptEnabled: true,
      autoAcceptDelayHours: 2,
      autoReplyEnabled: true,
      autoReplyMessages: ['Hi! 😊', 'How are you?', 'Thanks for showing interest!'],
      displayPriority: 1000,
      adminNotes: ''
    });
  };

  const filteredProfiles = dummyProfiles.filter(profile => {
    const query = searchQuery.toLowerCase();
    return (
      profile.userId.firstName.toLowerCase().includes(query) ||
      profile.userId.lastName.toLowerCase().includes(query) ||
      profile.userId.email.toLowerCase().includes(query) ||
      profile.profileId.city.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">Dummy Profiles Management</h2>
          <p className="text-gray-600 mt-1">
            Create and manage dummy profiles for user attraction
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedProfiles.length > 0 && (
            <>
              <Button 
                onClick={async () => {
                  try {
                    await apiClient.post('/dummy-profile/bulk-toggle', {
                      ids: selectedProfiles,
                      isActive: true
                    });
                    toast.success(`${selectedProfiles.length} profiles enabled`);
                    setSelectedProfiles([]);
                    fetchDummyProfiles();
                    fetchStats();
                  } catch (error) {
                    toast.error('Failed to enable profiles');
                  }
                }}
                variant="outline"
                size="sm"
              >
                <Power className="size-4 mr-2" />
                Enable ({selectedProfiles.length})
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    await apiClient.post('/dummy-profile/bulk-toggle', {
                      ids: selectedProfiles,
                      isActive: false
                    });
                    toast.success(`${selectedProfiles.length} profiles disabled`);
                    setSelectedProfiles([]);
                    fetchDummyProfiles();
                    fetchStats();
                  } catch (error) {
                    toast.error('Failed to disable profiles');
                  }
                }}
                variant="outline"
                size="sm"
              >
                <PowerOff className="size-4 mr-2" />
                Disable ({selectedProfiles.length})
              </Button>
            </>
          )}
          <Button 
            onClick={() => setShowBulkImportDialog(true)} 
            variant="outline"
          >
            <Upload className="size-4 mr-2" />
            Bulk Import
          </Button>
          <Button onClick={processAutoAccepts} variant="outline" disabled={loading}>
            <RefreshCw className="size-4 mr-2" />
            Process Auto-Accepts
          </Button>
          <Button onClick={() => setShowSearchDialog(true)} variant="outline">
            <Search className="size-4 mr-2" />
            Search Real Profile
          </Button>
          <Button onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}>
            <Plus className="size-4 mr-2" />
            Create New
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Dummies</p>
                <p className="text-2xl font-bold">{stats.totalDummies}</p>
              </div>
              <Users className="size-8 text-blue-500" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.activeDummies}</p>
              </div>
              <CheckCircle className="size-8 text-green-500" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Views</p>
                <p className="text-2xl font-bold">{stats.stats?.totalViews || 0}</p>
              </div>
              <Eye className="size-8 text-purple-500" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Interests Accepted</p>
                <p className="text-2xl font-bold">{stats.stats?.totalInterestsAccepted || 0}</p>
              </div>
              <TrendingUp className="size-8 text-orange-500" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Messages Sent</p>
                <p className="text-2xl font-bold">{stats.stats?.totalMessagesSent || 0}</p>
              </div>
              <MessageSquare className="size-8 text-pink-500" />
            </div>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 size-4" />
          <Input
            placeholder="Search by name, email, or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Profiles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && dummyProfiles.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <RefreshCw className="size-12 mx-auto mb-4 text-gray-400 animate-spin" />
            <p className="text-gray-600">Loading dummy profiles...</p>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Users className="size-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No dummy profiles found</p>
            <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
              <Plus className="size-4 mr-2" />
              Create First Dummy Profile
            </Button>
          </div>
        ) : (
          filteredProfiles.map((profile) => (
            <Card key={profile._id} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <Checkbox
                    checked={selectedProfiles.includes(profile._id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedProfiles([...selectedProfiles, profile._id]);
                      } else {
                        setSelectedProfiles(selectedProfiles.filter(id => id !== profile._id));
                      }
                    }}
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">
                      {profile.userId.firstName} {profile.userId.lastName}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {profile.profileId.age} yrs • {profile.profileId.city}
                    </p>
                  </div>
                </div>
                <Badge variant={profile.isActive ? 'default' : 'secondary'}>
                  {profile.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="size-4 text-gray-400" />
                  <span className="text-gray-600">Views: {profile.stats.totalViews}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="size-4 text-gray-400" />
                  <span className="text-gray-600">
                    Interests: {profile.stats.interestsReceived} / Accepted: {profile.stats.interestsAccepted}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MessageSquare className="size-4 text-gray-400" />
                  <span className="text-gray-600">
                    Messages: {profile.stats.messagesSent} sent
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                {profile.autoAcceptInterests.enabled && (
                  <Badge variant="outline" className="text-xs">
                    <Clock className="size-3 mr-1" />
                    Auto-Accept: {profile.autoAcceptInterests.delayHours}h
                  </Badge>
                )}
                {profile.autoReplyMessages.enabled && (
                  <Badge variant="outline" className="text-xs">
                    <MessageSquare className="size-3 mr-1" />
                    Auto-Reply
                  </Badge>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => openEditDialog(profile)}
                >
                  <Edit className="size-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleStatus(profile._id)}
                >
                  {profile.isActive ? <XCircle className="size-4" /> : <CheckCircle className="size-4" />}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteDummyProfile(profile._id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>

              {profile.adminNotes && (
                <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
                  <strong>Note:</strong> {profile.adminNotes}
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Search Real Profile Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Search Real Profile to Clone</DialogTitle>
            <DialogDescription>
              Search for an existing real profile to use as a template for your dummy profile
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, email, or phone..."
                value={profileSearchQuery}
                onChange={(e) => setProfileSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchRealProfiles()}
              />
              <Button onClick={searchRealProfiles} disabled={searchLoading}>
                <Search className="size-4 mr-2" />
                Search
              </Button>
            </div>

            {searchLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="size-12 mx-auto mb-4 text-gray-400 animate-spin" />
                <p className="text-gray-600">Searching profiles...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {searchResults.map((profile) => (
                  <Card key={profile._id} className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => createFromRealProfile(profile)}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">
                          {profile.userId.firstName} {profile.userId.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {profile.age} yrs • {profile.gender}
                        </p>
                      </div>
                      <Button size="sm" variant="outline">
                        <UserPlus className="size-4 mr-1" />
                        Clone
                      </Button>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>{profile.city}, {profile.state}</p>
                      <p>{profile.religion} • {profile.community}</p>
                      <p>{profile.occupation}</p>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="size-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">Search for profiles to get started</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          setSelectedProfile(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? 'Edit Dummy Profile' : 'Create Dummy Profile'}
            </DialogTitle>
            <DialogDescription>
              {showEditDialog 
                ? 'Update the dummy profile details and settings'
                : 'Fill in the details to create a new dummy profile'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="font-semibold">Location</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>State</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value, city: '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.IN.map(state => (
                        <SelectItem key={state.name} value={state.name}>{state.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>City</Label>
                  <Select value={formData.city} onValueChange={(value) => setFormData({ ...formData, city: value })} disabled={!formData.state}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.state && cities[formData.state] && cities[formData.state].map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="Enter country"
                  />
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div className="space-y-4">
              <h3 className="font-semibold">Profile Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Religion</Label>
                  <Select value={formData.religion} onValueChange={(value) => setFormData({ ...formData, religion: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select religion" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELIGION_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Community</Label>
                  <Input
                    value={formData.community}
                    onChange={(e) => setFormData({ ...formData, community: e.target.value })}
                    placeholder="Enter community"
                  />
                </div>
                <div>
                  <Label>Mother Tongue</Label>
                  <Select value={formData.motherTongue} onValueChange={(value) => setFormData({ ...formData, motherTongue: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select mother tongue" />
                    </SelectTrigger>
                    <SelectContent>
                      {MOTHER_TONGUE_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Occupation</Label>
                  <Input
                    value={formData.occupation}
                    onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                    placeholder="Enter occupation"
                  />
                </div>
                <div>
                  <Label>Marital Status</Label>
                  <Select value={formData.maritalStatus} onValueChange={(value) => setFormData({ ...formData, maritalStatus: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select marital status" />
                    </SelectTrigger>
                    <SelectContent>
                      {MARITAL_STATUS_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Employment Type</Label>
                  <Select value={formData.employmentType} onValueChange={(value) => setFormData({ ...formData, employmentType: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMPLOYMENT_TYPES.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Annual Income (Lakhs)</Label>
                  <Select value={formData.annualIncome} onValueChange={(value) => setFormData({ ...formData, annualIncome: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select income range" />
                    </SelectTrigger>
                    <SelectContent>
                      {INCOME_RANGES.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Height (cm)</Label>
                  <Select value={formData.height.toString()} onValueChange={(value) => setFormData({ ...formData, height: parseInt(value) })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select height" />
                    </SelectTrigger>
                    <SelectContent>
                      {HEIGHT_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Weight (kg)</Label>
                  <Input
                    type="number"
                    min="30"
                    max="200"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) })}
                    placeholder="Enter weight in kg"
                  />
                </div>
                <div>
                  <Label>Diet</Label>
                  <Select value={formData.diet} onValueChange={(value) => setFormData({ ...formData, diet: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select diet" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIET_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Smoking</Label>
                  <Select value={formData.smoking} onValueChange={(value) => setFormData({ ...formData, smoking: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select smoking habit" />
                    </SelectTrigger>
                    <SelectContent>
                      {HABIT_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Drinking</Label>
                  <Select value={formData.drinking} onValueChange={(value) => setFormData({ ...formData, drinking: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select drinking habit" />
                    </SelectTrigger>
                    <SelectContent>
                      {HABIT_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>About</Label>
                <Textarea
                  value={formData.about}
                  onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                  placeholder="Write about the profile..."
                  rows={3}
                />
              </div>

              {/* Photo Upload */}
              <div>
                <Label>Profile Photos</Label>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;

                        try {
                          const uploadPromises = files.map(file => {
                            return new Promise<string>((resolve, reject) => {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                resolve(reader.result as string);
                              };
                              reader.onerror = reject;
                              reader.readAsDataURL(file);
                            });
                          });

                          const base64Images = await Promise.all(uploadPromises);
                          setFormData({
                            ...formData,
                            photos: [...formData.photos, ...base64Images]
                          });
                          toast.success(`${files.length} photo(s) added`);
                        } catch (error) {
                          toast.error('Failed to process images');
                        }
                      }}
                      className="flex-1"
                    />
                  </div>

                  {/* Photo Preview Grid */}
                  {formData.photos.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {formData.photos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-24 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                photos: formData.photos.filter((_, i) => i !== index)
                              });
                              toast.success('Photo removed');
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="size-3" />
                          </button>
                          <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1 rounded">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-gray-600">
                    Upload multiple photos. First photo will be the primary photo.
                  </p>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <h3 className="font-semibold">Automation Settings</h3>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label>Active Status</Label>
                  <p className="text-sm text-gray-600">Show this profile in discovery</p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>

              <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Accept Interests</Label>
                    <p className="text-sm text-gray-600">Automatically accept interests after delay</p>
                  </div>
                  <Switch
                    checked={formData.autoAcceptEnabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, autoAcceptEnabled: checked })}
                  />
                </div>
                {formData.autoAcceptEnabled && (
                  <div>
                    <Label>Delay (Hours)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="48"
                      value={formData.autoAcceptDelayHours}
                      onChange={(e) => setFormData({ ...formData, autoAcceptDelayHours: parseInt(e.target.value) })}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Reply Messages</Label>
                    <p className="text-sm text-gray-600">Send automated messages after accepting</p>
                  </div>
                  <Switch
                    checked={formData.autoReplyEnabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, autoReplyEnabled: checked })}
                  />
                </div>
                {formData.autoReplyEnabled && (
                  <div className="space-y-2">
                    <Label>Reply Messages (one per line)</Label>
                    <Textarea
                      value={formData.autoReplyMessages.join('\n')}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        autoReplyMessages: e.target.value.split('\n').filter(m => m.trim())
                      })}
                      placeholder="Enter messages (one per line)"
                      rows={4}
                    />
                  </div>
                )}
              </div>

              <div>
                <Label>Display Priority</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.displayPriority}
                  onChange={(e) => setFormData({ ...formData, displayPriority: parseInt(e.target.value) })}
                />
                <p className="text-sm text-gray-600 mt-1">
                  Higher priority = appears after more real profiles
                </p>
              </div>

              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={formData.adminNotes}
                  onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
                  placeholder="Internal notes about this profile"
                  rows={3}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  setShowEditDialog(false);
                  setSelectedProfile(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={showEditDialog ? handleUpdateDummyProfile : handleCreateDummyProfile}
                disabled={loading}
              >
                {loading ? 'Saving...' : showEditDialog ? 'Update Profile' : 'Create Profile'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}